if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.thumbnailAnimationCleanup = null;
        this.ignoreSlideMediaId = null;
        this.ignoreSlideTimer = null;

        if (
          !this.elements.viewer ||
          !this.elements.thumbnails
        ) {
          return;
        }

        /*
         * Store only the real thumbnails.
         */
        this.originalThumbnailItems = Array.from(
          this.elements.thumbnails.querySelectorAll(
            '[data-target]:not([data-loop-clone])'
          )
        );

        this.injectArrowStyles();

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(
            this.onSlideChanged.bind(this),
            500
          )
        );

        /*
         * Handles clicks on real and cloned thumbnails.
         */
        this.elements.thumbnails.addEventListener(
          'click',
          (event) => {
            const clickedElement =
              event.target instanceof Element
                ? event.target
                : null;

            const thumbnail =
              clickedElement?.closest(
                '[data-target]'
              );

            if (
              !thumbnail ||
              !this.elements.thumbnails.contains(
                thumbnail
              )
            ) {
              return;
            }

            this.setActiveMedia(
              thumbnail.dataset.target,
              false,
              thumbnail,
              'thumbnail'
            );
          }
        );

        const initialMedia =
          this.elements.viewer.querySelector(
            '[data-media-id].is-active'
          ) ||
          this.elements.viewer.querySelector(
            '[data-media-id]'
          );

        if (initialMedia) {
          this.buildInfiniteThumbnailTrack(
            initialMedia.dataset.mediaId
          );
        }

        this.createNavigationArrows();

        if (
          this.dataset.desktopLayout?.includes(
            'thumbnail'
          ) &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      injectArrowStyles() {
        if (
          document.getElementById(
            'media-gallery-loop-arrow-styles'
          )
        ) {
          return;
        }

        const style =
          document.createElement('style');

        style.id =
          'media-gallery-loop-arrow-styles';

        style.textContent = `
          media-gallery .media-gallery-loop-controls {
            position: relative !important;
            box-sizing: border-box;
            padding-left: 48px;
            padding-right: 48px;
            overflow: visible;
          }

          media-gallery .media-gallery-loop-controls > .slider-button {
            display: none !important;
          }

          media-gallery .media-gallery-loop-arrow {
            position: absolute;
            top: 50%;
            z-index: 5;
            display: flex;
            width: 38px;
            height: 38px;
            align-items: center;
            justify-content: center;
            padding: 0;
            border: 1px solid rgba(0, 0, 0, 0.18);
            border-radius: 999px;
            background: #ffffff;
            color: #111111;
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
            transform: translateY(-50%);
            transition:
              background-color 150ms ease,
              border-color 150ms ease,
              box-shadow 150ms ease;
            touch-action: manipulation;
          }

          media-gallery .media-gallery-loop-arrow:hover {
            background: #f5f5f5;
            border-color: rgba(0, 0, 0, 0.35);
            box-shadow: 0 3px 10px rgba(0, 0, 0, 0.18);
          }

          media-gallery .media-gallery-loop-arrow:active {
            background: #eeeeee;
          }

          media-gallery .media-gallery-loop-arrow:focus-visible {
            outline: 2px solid currentColor;
            outline-offset: 2px;
          }

          media-gallery .media-gallery-loop-arrow--previous {
            left: 3px;
          }

          media-gallery .media-gallery-loop-arrow--next {
            right: 3px;
          }

          media-gallery .media-gallery-loop-arrow svg {
            display: block;
            width: 20px;
            height: 20px;
            pointer-events: none;
          }

          @media screen and (max-width: 749px) {
            media-gallery .media-gallery-loop-controls {
              padding-left: 42px;
              padding-right: 42px;
            }

            media-gallery .media-gallery-loop-arrow {
              width: 34px;
              height: 34px;
            }

            media-gallery .media-gallery-loop-arrow svg {
              width: 18px;
              height: 18px;
            }
          }
        `;

        document.head.appendChild(style);
      }

      createNavigationArrows() {
        if (
          this.originalThumbnailItems.length < 2 ||
          this.elements.thumbnails.querySelector(
            '.media-gallery-loop-arrow'
          )
        ) {
          return;
        }

        this.elements.thumbnails.classList.add(
          'media-gallery-loop-controls'
        );

        const previousArrow =
          this.createArrowButton('previous');

        const nextArrow =
          this.createArrowButton('next');

        previousArrow.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();

            this.navigateByOffset(-1);
          }
        );

        nextArrow.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();

            this.navigateByOffset(1);
          }
        );

        this.elements.thumbnails.append(
          previousArrow,
          nextArrow
        );
      }

      createArrowButton(direction) {
        const button =
          document.createElement('button');

        const isPrevious =
          direction === 'previous';

        button.type = 'button';

        button.className =
          `media-gallery-loop-arrow media-gallery-loop-arrow--${direction}`;

        button.setAttribute(
          'aria-label',
          isPrevious
            ? 'Previous product image'
            : 'Next product image'
        );

        button.setAttribute(
          'title',
          isPrevious
            ? 'Previous image'
            : 'Next image'
        );

        button.innerHTML = isPrevious
          ? `
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M15 18L9 12L15 6"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </svg>
          `
          : `
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M9 6L15 12L9 18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </svg>
          `;

        return button;
      }

      /*
       * Arrow navigation does not rebuild the carousel.
       * It simply moves to the adjacent DOM thumbnail.
       */
      navigateByOffset(offset) {
        if (
          this.originalThumbnailItems.length < 2 ||
          this.thumbnailAnimationCleanup
        ) {
          return;
        }

        const currentThumbnail =
          this.getActiveThumbnail();

        if (!currentThumbnail) return;

        let targetThumbnail =
          offset < 0
            ? currentThumbnail.previousElementSibling
            : currentThumbnail.nextElementSibling;

        while (
          targetThumbnail &&
          !targetThumbnail.matches(
            '[data-target]'
          )
        ) {
          targetThumbnail =
            offset < 0
              ? targetThumbnail.previousElementSibling
              : targetThumbnail.nextElementSibling;
        }

        if (!targetThumbnail) return;

        this.setActiveMedia(
          targetThumbnail.dataset.target,
          false,
          targetThumbnail,
          'arrow',
          currentThumbnail
        );
      }

      getActiveThumbnail() {
        return (
          this.elements.thumbnails
            .querySelector(
              'button[aria-current="true"]'
            )
            ?.closest('[data-target]') || null
        );
      }

      getThumbnailList() {
        return (
          this.elements.thumbnails?.slider ||
          this.elements.thumbnails?.querySelector(
            '[id^="Slider-Thumbnails"]'
          ) ||
          null
        );
      }

      getRealThumbnail(mediaId) {
        return (
          this.getThumbnailList()?.querySelector(
            `[data-target="${mediaId}"]:not([data-loop-clone])`
          ) || null
        );
      }

      /*
       * Prevent the main image's slideChanged event from
       * running a second thumbnail animation.
       */
      suppressSlideChanged(mediaId) {
        this.ignoreSlideMediaId = mediaId;

        window.clearTimeout(
          this.ignoreSlideTimer
        );

        this.ignoreSlideTimer =
          window.setTimeout(() => {
            if (
              this.ignoreSlideMediaId === mediaId
            ) {
              this.ignoreSlideMediaId = null;
            }
          }, 1400);
      }

      onSlideChanged(event) {
        const mediaId =
          event.detail?.currentElement?.dataset
            ?.mediaId;

        if (!mediaId) return;

        /*
         * Arrow and thumbnail clicks already handle
         * their own thumbnail movement.
         */
        if (
          this.ignoreSlideMediaId === mediaId
        ) {
          return;
        }

        /*
         * Swiping the main product image behaves like
         * directly selecting a thumbnail.
         */
        const thumbnail =
          this.getRealThumbnail(mediaId);

        if (thumbnail) {
          this.animateDirectThumbnailSelection(
            mediaId,
            thumbnail
          );
        }
      }

      createThumbnailClone(
        originalThumbnail,
        cloneType
      ) {
        const clone =
          originalThumbnail.cloneNode(true);

        clone.setAttribute(
          'data-loop-clone',
          cloneType
        );

        clone.removeAttribute('id');

        clone
          .querySelectorAll('[id]')
          .forEach((element) => {
            element.removeAttribute('id');
          });

        clone
          .querySelectorAll('[aria-current]')
          .forEach((element) => {
            element.removeAttribute(
              'aria-current'
            );
          });

        return clone;
      }

      /*
       * Builds three complete copies:
       *
       * before clones | real thumbnails | after clones
       *
       * The selected real thumbnail begins in the middle copy.
       */
      buildInfiniteThumbnailTrack(mediaId) {
        const thumbnailList =
          this.getThumbnailList();

        if (
          !thumbnailList ||
          !this.originalThumbnailItems.length
        ) {
          return;
        }

        const selectedIndex =
          this.originalThumbnailItems.findIndex(
            (thumbnail) =>
              thumbnail.dataset.target === mediaId
          );

        if (selectedIndex === -1) return;

        /*
         * Clicking a thumbnail rotates it into position 1.
         */
        const rotatedOriginals = [
          ...this.originalThumbnailItems.slice(
            selectedIndex
          ),
          ...this.originalThumbnailItems.slice(
            0,
            selectedIndex
          ),
        ];

        const beforeClones =
          rotatedOriginals.map((thumbnail) =>
            this.createThumbnailClone(
              thumbnail,
              'before'
            )
          );

        const afterClones =
          rotatedOriginals.map((thumbnail) =>
            this.createThumbnailClone(
              thumbnail,
              'after'
            )
          );

        Array.from(
          thumbnailList.children
        ).forEach((child) => {
          if (
            child.matches('[data-target]')
          ) {
            child.remove();
          }
        });

        thumbnailList.append(
          ...beforeClones,
          ...rotatedOriginals,
          ...afterClones
        );

        if (
          typeof this.elements.thumbnails
            .resetPages === 'function'
        ) {
          this.elements.thumbnails.resetPages();
        }

        const activeThumbnail =
          rotatedOriginals[0];

        const startingLeft =
          activeThumbnail.offsetLeft;

        /*
         * Begin in the middle copy.
         */
        thumbnailList.scrollLeft =
          startingLeft;

        window.requestAnimationFrame(() => {
          thumbnailList.scrollLeft =
            startingLeft;
        });

        this.setActiveThumbnail(
          activeThumbnail,
          false
        );
      }

      cancelThumbnailAnimation() {
        if (!this.thumbnailAnimationCleanup) {
          return;
        }

        this.thumbnailAnimationCleanup();
        this.thumbnailAnimationCleanup = null;
      }

      smoothThumbnailScroll(targetLeft, onComplete) {
  const thumbnailList =
    this.getThumbnailList();

  if (!thumbnailList) return;

  this.cancelThumbnailAnimation();

  thumbnailList.style.setProperty(
    'scroll-behavior',
    'auto',
    'important'
  );

  thumbnailList.scrollLeft = targetLeft;

  this.thumbnailAnimationCleanup = null;

  onComplete?.();
}

      /*
       * Directly clicking a thumbnail:
       *
       * Smoothly move it to position 1, then rebuild
       * the invisible copies around it.
       */
      animateDirectThumbnailSelection(
        mediaId,
        clickedThumbnail
      ) {
        if (!clickedThumbnail) return;

        this.setActiveThumbnail(
          clickedThumbnail,
          false
        );

        this.smoothThumbnailScroll(
          clickedThumbnail.offsetLeft,
          () => {
            this.buildInfiniteThumbnailTrack(
              mediaId
            );
          }
        );
      }

      /*
       * Arrow navigation:
       *
       * Moves by exactly one thumbnail.
       * It does not rotate the selected thumbnail
       * into position 1.
       */
      animateArrowNavigation(
        currentThumbnail,
        targetThumbnail
      ) {
        const thumbnailList =
          this.getThumbnailList();

        if (
          !thumbnailList ||
          !currentThumbnail ||
          !targetThumbnail
        ) {
          return;
        }

        this.setActiveThumbnail(
          targetThumbnail,
          false
        );

        /*
         * Preserve the selected thumbnail's screen position.
         *
         * Left arrow gives a negative movement, causing
         * the thumbnail strip to scroll to the right.
         */
        const movement =
          targetThumbnail.offsetLeft -
          currentThumbnail.offsetLeft;

        const destination =
          thumbnailList.scrollLeft +
          movement;

        this.smoothThumbnailScroll(
          destination,
          () => {
            const cloneType =
              targetThumbnail.getAttribute(
                'data-loop-clone'
              );

            /*
             * Normal real thumbnails need no correction.
             */
            if (!cloneType) {
              this.setActiveThumbnail(
                targetThumbnail,
                false
              );

              return;
            }

            /*
             * When we reach a cloned copy, silently move
             * to the identical real thumbnail in the middle.
             */
            const realThumbnail =
              this.getRealThumbnail(
                targetThumbnail.dataset.target
              );

            if (!realThumbnail) return;

            /*
             * Record where the cloned thumbnail is currently
             * displayed inside the visible carousel.
             */
            const viewportOffset =
              targetThumbnail.offsetLeft -
              thumbnailList.scrollLeft;

            /*
             * Move to the real thumbnail while preserving
             * that exact visible position.
             *
             * Because the surrounding sequence is identical,
             * the customer cannot see this correction.
             */
            thumbnailList.scrollLeft =
              realThumbnail.offsetLeft -
              viewportOffset;

            this.setActiveThumbnail(
              realThumbnail,
              false
            );
          }
        );
      }

      setActiveMedia(
        mediaId,
        prepend,
        clickedThumbnail = null,
        navigationSource = 'thumbnail',
        currentThumbnail = null
      ) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          );

        if (!activeMedia) return;

        this.suppressSlideChanged(mediaId);

        this.elements.viewer
          .querySelectorAll(
            '[data-media-id]'
          )
          .forEach((element) => {
            element.classList.remove(
              'is-active'
            );
          });

        activeMedia.classList.add(
          'is-active'
        );

        if (prepend) {
          activeMedia.parentElement.prepend(
            activeMedia
          );

          const realThumbnail =
            this.originalThumbnailItems.find(
              (thumbnail) =>
                thumbnail.dataset.target ===
                mediaId
            );

          if (realThumbnail) {
            const originalIndex =
              this.originalThumbnailItems.indexOf(
                realThumbnail
              );

            if (originalIndex > 0) {
              this.originalThumbnailItems.splice(
                originalIndex,
                1
              );

              this.originalThumbnailItems.unshift(
                realThumbnail
              );
            }
          }

          if (
            this.elements.viewer.slider &&
            typeof this.elements.viewer
              .resetPages === 'function'
          ) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
  const mediaSlider =
    activeMedia.parentElement;

  mediaSlider.style.setProperty(
    'scroll-behavior',
    'auto',
    'important'
  );

  mediaSlider.scrollLeft =
    activeMedia.offsetLeft;
});

        this.playActiveMedia(activeMedia);

        if (prepend) {
          this.buildInfiniteThumbnailTrack(
            mediaId
          );
        } else if (
          navigationSource === 'arrow'
        ) {
          /*
           * Arrow navigation only scrolls one place.
           * It never rebuilds the thumbnail list.
           */
          this.animateArrowNavigation(
            currentThumbnail,
            clickedThumbnail
          );
        } else {
          /*
           * Direct thumbnail clicks still move the
           * selected thumbnail into position 1.
           */
          this.animateDirectThumbnailSelection(
            mediaId,
            clickedThumbnail
          );
        }

        const realThumbnail =
          this.originalThumbnailItems.find(
            (thumbnail) =>
              thumbnail.dataset.target ===
              mediaId
          ) || null;

        if (realThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            realThumbnail.dataset
              .mediaPosition
          );
        }
      }

      setActiveThumbnail(
        thumbnail,
        shouldScroll = true
      ) {
        if (
          !this.elements.thumbnails ||
          !thumbnail
        ) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll(
            '[data-target] button'
          )
          .forEach((button) => {
            button.removeAttribute(
              'aria-current'
            );
          });

        const button =
          thumbnail.querySelector('button');

        if (button) {
          button.setAttribute(
            'aria-current',
            'true'
          );
        }

        if (!shouldScroll) return;

        if (
          typeof this.elements.thumbnails
            .isSlideVisible === 'function' &&
          this.elements.thumbnails.isSlideVisible(
            thumbnail,
            10
          )
        ) {
          return;
        }

        const thumbnailList =
          this.getThumbnailList();

        if (!thumbnailList) return;

        thumbnailList.style.setProperty(
  'scroll-behavior',
  'auto',
  'important'
);

thumbnailList.scrollLeft =
  thumbnail.offsetLeft;
      }

      announceLiveRegion(
        activeItem,
        position
      ) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (
          !image ||
          !this.elements.liveRegion
        ) {
          return;
        }

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings
              .imageAvailable
              .replace(
                '[index]',
                position
              );

          window.setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector(
            '.deferred-media'
          );

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector(
            'sticky-header'
          );

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event(
            'preventHeaderReveal'
          )
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) {
          return;
        }

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) => {
            slide.setAttribute(
              'role',
              'presentation'
            );
          }
        );
      }
    }
  );
}